import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';
import { InputTypes } from '@lib/components/Input/enums';
import {
  getAttachmentsValidation,
  getCodeValidation,
  getFieldValidation,
  getOptionsArrayValidation,
  getOptionValidation,
  getTimeValidation,
} from '@lib/utils/validationUtils';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import ChipValueContainer from '@lib/components/Select/components/ChipValueContainer';
import { GetLoadOptions } from '@lib/components/Select/types';
import formStyles from '@lib/assets/styles/Form.module.scss';
import UserChipListField from '@lib/components/UserChipListField/UserChipListField';
import AreaOption from '@lib/components/Select/components/AreaOption';
import ChipSingleValue from '@lib/components/Select/components/ChipSingleValue';
import AssetOption from '@lib/components/Select/components/AssetOption';
import AssetSingleValue from '@lib/components/Select/components/AssetSingleValue';
import SiteOption from '@lib/components/Select/components/SiteOption';
import { getInterventionPriorityOptions } from '@lib/utils/priority';
import UserOption from '@lib/components/Select/components/UserOption';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import DatePicker from '@lib/components/DateTimePicker/DatePicker';
import { getInterventionStatusOptions } from '@lib/utils/status';
import { TIME_MASK } from '@lib/components/Input/masks';
import ChipOption from '@lib/components/Select/components/ChipOption';
import FilesUploader from '@lib/components/FilesUploader/FilesUploader';
import {
  fileSizeLimit,
  FileSizeLimits,
  UppyDocumentationAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import { ButtonSizes } from '@lib/components/Button/Button';
import { FilesPreview } from '@lib/components/FilesUploader/enums';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import getTaskAndCompletionsFormFields from '@lib/components/TaskCompletions/TaskAndCompletionsModal/getTaskAndCompletionsFormFields';

const getDefaultSelectEntityBySiteIdProps = (isEditForm: boolean) => ({
  isUseWatch: true,
  isDisabledFn: isEditForm
    ? undefined
    : (values: Values) =>
        _get(values, `${FieldNames.Site}.value`) === undefined,
  getFieldKeyFn: isEditForm
    ? undefined
    : (values: Values, name?: string) => {
        const selectedSiteId = _get(values, `${FieldNames.Site}.value`);
        if (selectedSiteId) return `${name}-site-${selectedSiteId}`;
        return FieldNames.Site;
      },
});

const getDefaultSelectAssignmentFieldProps = (isEditForm: boolean) => ({
  isUseWatch: true,
  isDisabledFn: isEditForm
    ? undefined
    : (values: Values) =>
        _get(values, `${FieldNames.Site}.value`) === undefined ||
        _get(values, `${FieldNames.Category}.value`) === undefined,
  getFieldKeyFn: isEditForm
    ? undefined
    : (values: Values, name?: string) => {
        const selectedSiteId = _get(values, `${FieldNames.Site}.value`);
        const selectedCategoryId = _get(values, `${FieldNames.Category}.value`);
        if (selectedSiteId && selectedCategoryId) {
          return `${name}-site-${selectedSiteId}-category-${selectedCategoryId}`;
        }
        return `${FieldNames.Site}-${FieldNames.Category}`;
      },
});

const multiSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
};

type Args = {
  assigneeIds: { id: string }[];
  interventionId?: string;
  getAssetsLoadOptions: GetLoadOptions;
  // Admin-only: the Departments & visibility block is dropped without it.
  getDepartmentsLoadOptions?: GetLoadOptions;
  getInterventionCategoriesLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getSupervisorLoadOptions: GetLoadOptions;
  getAssigneeLoadOptions: GetLoadOptions;
  getTasksLoadOptions: GetLoadOptions;
  isEditForm: boolean;
  // Operational users without Tasks access can't pick a task / completions.
  isTasksAvailable: boolean;
  // Admin-only: opens the assets modal instead of the Asset select's menu.
  openAssetsModalTableMultiselect?: () => void;
  // Admin-only: adds "Create new" to the Category select.
  openCategoryFormModal?: () => void;
  openCompletionsStep: () => void;
  openDepartmentsModalTableMultiselect?: () => void;
  openTaskStep: () => void;
  t: TFunction<'translation', undefined>;
  isCategoriesViewOnly?: boolean;
  // User app (offline-capable) tweaks.
  showOfflineMessage?: boolean;
  hideAssigneeListErrorsSpace?: boolean;
  // Mirror the fresh Site / Site Area / Asset into useTaskAndCompletionsPicker, which
  // can't rely on `formRef` staying in sync with RHF.
  onSiteChange?: (site: { value?: string; label?: string } | null) => void;
  onSiteAreaChange?: (
    siteArea: { value?: string; label?: string } | null,
  ) => void;
  onAssetChange?: (asset: { value?: string; label?: string } | null) => void;
};

export default function getInterventionFormFields(args: Args): FieldItem[] {
  const {
    assigneeIds,
    getAssetsLoadOptions,
    getDepartmentsLoadOptions,
    getInterventionCategoriesLoadOptions,
    getSiteAreasLoadOptions,
    getSupervisorLoadOptions,
    getAssigneeLoadOptions,
    getSitesLoadOptions,
    getTasksLoadOptions,
    isEditForm,
    isTasksAvailable,
    openAssetsModalTableMultiselect,
    openCategoryFormModal,
    openCompletionsStep,
    openDepartmentsModalTableMultiselect,
    openTaskStep,
    interventionId,
    isCategoriesViewOnly,
    onSiteChange,
    onSiteAreaChange,
    onAssetChange,
    showOfflineMessage,
    hideAssigneeListErrorsSpace,
    t,
  } = args;
  const defaultSelectEntityBySiteIdProps =
    getDefaultSelectEntityBySiteIdProps(isEditForm);
  const defaultSelectAssignmentFieldProps =
    getDefaultSelectAssignmentFieldProps(isEditForm);
  return [
    {
      name: FieldNames.DetailsTitle,
      customRender: () => (
        <FieldTitleRender key="details" title={t('details')} />
      ),
    },
    {
      name: FieldNames.Name,
      label: t('intervention-name'),
      element: Input,
      validation: getFieldValidation({ required: true, t }),
    },
    {
      name: FieldNames.Description,
      label: t('description'),
      element: TextEditor,
      validation: getFieldValidation({
        required: true,
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
    },
    {
      name: FieldNames.AttachmentsAttached,
      label: '',
      validation: getAttachmentsValidation({ t }),
      element: FilesUploader,
      componentProps: {
        showOfflineMessage,
        filesView: FilesPreview.Documents,
        allowedFileTypes: UppyDocumentationAllowedFileTypes,
        note: t('files-upload-validation-msg', {
          sizeImg: FileSizeLimits.Image,
          sizePdf: FileSizeLimits.File,
        }),
        maxFileSize: fileSizeLimit,
        uploadButtonProps: {
          buttonText: t('upload-files'),
          buttonSize: ButtonSizes.large,
          leftIcon: 'upload',
          fullWidth: true,
        },
      },
    },
    {
      name: FieldNames.Code,
      label: t('intervention-code'),
      element: Input,
      // Read-only fields in edit mode carry no validation: a value the user
      // can't change must not be able to block the submit.
      validation: isEditForm ? undefined : getCodeValidation({ t, max: 255 }),
      componentProps: {
        disabled: isEditForm,
      },
    },
    {
      name: FieldNames.Category,
      label: t('category'),
      element: Select,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getInterventionCategoriesLoadOptions,
        defaultOptions: false,
        createNewOptionProps:
          !openCategoryFormModal || isCategoriesViewOnly
            ? undefined
            : {
                title: t('create-new'),
                onCLick: openCategoryFormModal,
              },
        components: openCategoryFormModal
          ? { ValueContainer: ChipValueContainer, Option: ChipOption }
          : { ValueContainer: ChipValueContainer },
      },
      fieldChangeCallBack: ({ setValue, name }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.Category) {
          setValue(FieldNames.Supervisor, null, { shouldValidate: false });
          setValue(FieldNames.Assignee, null, { shouldValidate: false });
        }
      },
    },
    {
      name: FieldNames.Priority,
      label: t('priority'),
      element: Select,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getInterventionPriorityOptions(),
        isSearchable: false,
        isClearable: false,
        components: {
          ValueContainer: ChipValueContainer,
          Option: ChipOption,
        },
      },
      formItemClassName: formStyles.formColumn,
    },
    {
      name: FieldNames.Status,
      label: t('status'),
      element: Select,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getInterventionStatusOptions(),
        isSearchable: false,
        isClearable: false,
        components: {
          ValueContainer: ChipValueContainer,
          Option: ChipOption,
        },
      },
      formItemClassName: formStyles.formColumn,
    },
    {
      name: FieldNames.SubjectsTitle,
      customRender: () => (
        <FieldTitleRender key="subjects" title={t('subjects')} isBlockStart />
      ),
    },
    {
      name: FieldNames.Site,
      label: t('site'),
      element: Select,
      validation: isEditForm
        ? undefined
        : getOptionValidation({ required: true, t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getSitesLoadOptions,
        defaultOptions: false,
        disabled: isEditForm,
        components: {
          Option: SiteOption,
          SingleValue: ChipSingleValue,
        },
      },
      formItemClassName: formStyles.formColumn,
      fieldChangeCallBack: ({
        setValue,
        name,
        values,
      }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.Site) {
          setValue(FieldNames.SiteArea, null, { shouldValidate: false });
          setValue(FieldNames.Asset, null, { shouldValidate: false });
          setValue(FieldNames.Task, null, { shouldValidate: false });
          setValue(FieldNames.Completions, [], { shouldValidate: false });
          setValue(FieldNames.Supervisor, null, { shouldValidate: false });
          setValue(FieldNames.Assignee, null, { shouldValidate: false });
          onSiteChange?.(_get(values, FieldNames.Site) ?? null);
        }
      },
    },
    {
      name: FieldNames.SiteArea,
      label: t('area'),
      element: Select,
      validation: isEditForm ? undefined : getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getSiteAreasLoadOptions,
        defaultOptions: false,
        components: {
          Option: AreaOption,
          SingleValue: ChipSingleValue,
        },
        disabled: isEditForm,
        disabledTooltipProps: isEditForm
          ? undefined
          : {
              body: t('choose-site-first'),
            },
      },
      formItemClassName: formStyles.formColumn,
      ...defaultSelectEntityBySiteIdProps,
      fieldChangeCallBack: ({
        setValue,
        name,
        values,
      }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.SiteArea) {
          setValue(FieldNames.Task, null, { shouldValidate: false });
          setValue(FieldNames.Asset, null, { shouldValidate: false });
          setValue(FieldNames.Completions, [], { shouldValidate: false });
          onSiteAreaChange?.(_get(values, FieldNames.SiteArea) ?? null);
        }
      },
    },
    {
      name: FieldNames.Asset,
      label: t('related-equipment'),
      element: Select,
      validation: isEditForm ? undefined : getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getAssetsLoadOptions,
        defaultOptions: false,
        ...(openAssetsModalTableMultiselect && {
          isClearable: true,
          isSearchable: false,
          openMenuOnClick: false,
          openMenuOnFocus: false,
          menuIsOpen: false,
          onSelectWrapperMouseDown: openAssetsModalTableMultiselect,
        }),
        components: {
          Option: AssetOption,
          SingleValue: AssetSingleValue,
        } as unknown as SelectComponents,
        disabled: isEditForm,
        disabledTooltipProps: isEditForm
          ? undefined
          : {
              body: t('choose-site-first'),
            },
      } as SelectProps,
      ...defaultSelectEntityBySiteIdProps,
      getFieldKeyFn: isEditForm
        ? undefined
        : (values: Values, name?: string) => {
            const selectedSiteId = _get(values, `${FieldNames.Site}.value`, '');
            const selectedSiteAreaId = _get(
              values,
              `${FieldNames.SiteArea}.value`,
              '',
            );
            if (!!selectedSiteId || !!selectedSiteAreaId) {
              return `${name}-site-${selectedSiteId}-area-${selectedSiteAreaId}`;
            }
            return FieldNames.Site;
          },
      fieldChangeCallBack: ({
        setValue,
        name,
        values,
      }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.Asset) {
          setValue(FieldNames.Task, null, { shouldValidate: false });
          setValue(FieldNames.Completions, [], { shouldValidate: false });
          onAssetChange?.(_get(values, FieldNames.Asset) ?? null);
        }
      },
    },
    ...(isTasksAvailable
      ? getTaskAndCompletionsFormFields({
          t,
          isEditForm,
          getTasksLoadOptions,
          openTaskStep,
          openCompletionsStep,
        })
      : []),
    ...(getDepartmentsLoadOptions
      ? [
          {
            name: FieldNames.DepartmentsAndVisibilityTitle,
            customRender: () => (
              <FieldTitleRender
                key="departments-and-visibility"
                title={t('departments-and-visibility')}
                tooltipText={t('departments-and-visibility-intervention-desc')}
                isBlockStart
              />
            ),
          },
          {
            name: FieldNames.DepartmentLinks,
            label: t('intervention-departments-label'),
            element: Select,
            description: t('intervention-departments-leave-empty-desc'),
            componentProps: {
              isAsync: true,
              isMulti: true,
              getLoadOptions: getDepartmentsLoadOptions,
              defaultOptions: false,
              isClearable: true,
              isSearchable: false,
              openMenuOnClick: false,
              openMenuOnFocus: false,
              menuIsOpen: false,
              onSelectWrapperMouseDown: openDepartmentsModalTableMultiselect,
              components: multiSelectComponents,
            },
            isOptionalFn: () => true,
          },
        ]
      : []),
    {
      name: FieldNames.AssignationTitle,
      customRender: () => (
        <FieldTitleRender
          key="assignation"
          title={t('assignation')}
          isBlockStart
        />
      ),
    },
    {
      name: FieldNames.Supervisor,
      label: t('supervisor'),
      element: Select,
      validation: getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getSupervisorLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          SingleValue: UserSingleValue,
        } as unknown as SelectComponents,
        disabledTooltipProps: {
          body: t('choose-site-and-category-first'),
        },
      } as SelectProps,
      ...defaultSelectAssignmentFieldProps,
    },
    {
      name: FieldNames.Assignee,
      label: t('assignees-label'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getAssigneeLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
        disabledTooltipProps: {
          body: t('choose-site-and-category-first'),
        },
      } as SelectProps,
      hideErrorsSpace: true,
      ...defaultSelectAssignmentFieldProps,
    },
    {
      name: FieldNames.AssigneeList,
      element: UserChipListField,
      componentProps: {
        fieldName: FieldNames.Assignee,
        interventionId,
        ids: assigneeIds,
      },
      isUseWatch: true,
      hideErrorsSpace: hideAssigneeListErrorsSpace,
    },
    {
      name: FieldNames.TimingTitle,
      customRender: () => (
        <FieldTitleRender key="timing" title={t('timing')} isBlockStart />
      ),
    },
    {
      name: FieldNames.EstimatedTime,
      label: `${t('estimated-time')} hh:mm`,
      validation: getTimeValidation({ t }),
      element: Input,
      formItemClassName: formStyles.formColumn,
      componentProps: {
        mask: TIME_MASK,
      },
    },
    {
      name: FieldNames.EstimatedDate,
      label: t('estimated-date'),
      element: DatePicker,
      componentProps: {
        type: InputTypes.date,
      },
      validation: getFieldValidation({ t }),
      formItemClassName: formStyles.formColumn,
    },
    {
      name: FieldNames.LogisticTime,
      label: `${t('logistic-time')} hh:mm`,
      validation: getTimeValidation({ t }),
      element: Input,
      formItemClassName: formStyles.formColumn,
      componentProps: {
        mask: TIME_MASK,
      },
    },
    {
      name: FieldNames.DurationTime,
      label: `${t('time-spent')} hh:mm`,
      description: t('time-spent-description'),
      validation: getTimeValidation({ t }),
      element: Input,
      formItemClassName: formStyles.formColumn,
      componentProps: {
        mask: TIME_MASK,
      },
    },
  ];
}
